import React, { useEffect, useState } from 'react';
import { Card, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTags } from '@fortawesome/free-solid-svg-icons';
import { getExploreTags, getExploreSongs } from '../../services/exploreApi';
import Pagination from '../common/Pagination';

const ExploreMoodsGenres = () => {
  const [tags, setTags] = useState({ genres: [], moods: [] });
  const [songs, setSongs] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [songsLoading, setSongsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchTags = async () => {
    setTagsLoading(true);
    setError(null);
    try {
      const res = await getExploreTags();
      const d = res.data?.data || {};
      setTags({ genres: d.genres || [], moods: d.moods || [] });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load tags');
    } finally {
      setTagsLoading(false);
    }
  };

  const fetchSongs = async () => {
    if (!selectedGenre && !selectedMood) {
      setSongs([]);
      setTotal(0);
      return;
    }
    setSongsLoading(true);
    setError(null);
    try {
      const params = { page, items_per_page: pageSize };
      if (selectedGenre) params.genre = selectedGenre;
      if (selectedMood) params.mood = selectedMood;
      const res = await getExploreSongs(params);
      setSongs(res.data?.data || []);
      const objs = res.data?.objects;
      setTotal(objs?.total ?? objs?.count ?? 0);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed');
      setSongs([]);
      setTotal(0);
    } finally {
      setSongsLoading(false);
    }
  };

  useEffect(() => { fetchTags(); }, []);
  useEffect(() => {
    fetchSongs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenre, selectedMood, page]);

  const selectGenre = (v) => {
    setSelectedGenre(v === selectedGenre ? null : v);
    setPage(1);
  };

  const selectMood = (v) => {
    setSelectedMood(v === selectedMood ? null : v);
    setPage(1);
  };

  return (
    <div>
      <h2 className="mb-4">
        <FontAwesomeIcon icon={faTags} className="me-2" />
        Moods & Genres
      </h2>
      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      <Card className="mb-4">
        <Card.Header>Tags</Card.Header>
        <Card.Body>
          {tagsLoading ? <Spinner animation="border" size="sm" /> : (
            <>
              <div className="mb-3">
                <strong className="me-2">Genres:</strong>
                {tags.genres.length === 0 ? <span className="text-muted">None</span> : (
                  tags.genres.map((g) => (
                    <Badge
                      key={g.value || g}
                      bg="success"
                      className={`me-4 mb-2 p-2 text-dark ${selectedGenre === (g.value ?? g) ? 'border border-2 border-primary' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => selectGenre(g.value ?? g)}
                    >
                      {g.label ?? g.value ?? g}
                    </Badge>
                  ))
                )}
              </div>
              <div>
                <strong className="me-2">Moods:</strong>
                {tags.moods.length === 0 ? <span className="text-muted">None</span> : (
                  tags.moods.map((m) => (
                    <Badge
                      key={m.value || m}
                      bg="success"
                      className={`me-4 mb-2 p-2 text-dark ${selectedMood === (m.value ?? m) ? 'border border-2 border-primary' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => selectMood(m.value ?? m)}
                    >
                      {m.label ?? m.value ?? m}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          Songs {selectedGenre && `(Genre: ${selectedGenre})`} {selectedMood && `(Mood: ${selectedMood})`}
          {!selectedGenre && !selectedMood && ' — Select a genre and/or mood above'}
        </Card.Header>
        <Card.Body>
          {!selectedGenre && !selectedMood ? (
            <p className="text-muted">Click one or more genre/mood tags to view songs</p>
          ) : songsLoading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : songs.length === 0 ? (
            <p className="text-muted text-center py-4">No songs found</p>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Task ID</th>
                    <th>Title</th>
                    <th>Creator</th>
                    <th>Genre</th>
                    <th>Mood</th>
                    <th>Public at</th>
                  </tr>
                </thead>
                <tbody>
                  {songs.map((s, idx) => (
                    <tr key={s.task_id || idx}>
                      <td><code>{s.task_id}</code></td>
                      <td>{s.title || '—'}</td>
                      <td>{s.creator_display_name || s.creator_user_id || '—'}</td>
                      <td>{s.genre || '—'}</td>
                      <td>{Array.isArray(s.mood) ? s.mood.join(', ') : s.mood || '—'}</td>
                      <td>{s.public_at ? new Date(s.public_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination currentPage={page} totalItems={total} itemsPerPage={pageSize} onPageChange={setPage} />
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ExploreMoodsGenres;
